import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { productsApi } from '../api';
import { useCartStore } from '../store';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { ShoppingCart, Package, Minus, Plus, Loader2, ArrowLeft } from 'lucide-react';
import ProductImage from '../components/ProductImage';

const formatMoney = (amount) => new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(amount);

export default function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState(false);
  const { addToCart } = useCartStore();

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await productsApi.getOne(id);
      setProduct(response.data);
    } catch (error) {
      toast.error('Product not found');
      navigate('/');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddToCart = async () => {
    setAddingToCart(true);
    const result = await addToCart(product.id, quantity);
    if (result.success) {
      toast.success('Added to cart!');
    } else {
      toast.error(result.error);
    }
    setAddingToCart(false);
  };

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#FFD700]" /></div>;
  if (!product) return null;

  return (
    <div className="space-y-6">
      <Button variant="ghost" onClick={() => navigate(-1)} className="text-zinc-400 hover:text-white">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back
      </Button>

      <div className="grid md:grid-cols-2 gap-8">
        <Card className="bg-[#0A0A0A] border-white/10 overflow-hidden">
          <div className="h-64 md:h-96 bg-[#121212] relative">
            <ProductImage 
              src={product.thumbnail} 
              alt={product.title}
              className="w-full h-full object-cover"
              iconSize="w-24 h-24"
            />
            {product.has_discount && <Badge className="absolute top-4 right-4 bg-red-500 z-10">-{product.discount}%</Badge>}
          </div>
        </Card>

        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold text-white font-['Outfit']">{product.title}</h1>
            {product.subtitle && <p className="text-zinc-400 mt-2">{product.subtitle}</p>}
            {product.category_name && <Badge variant="outline" className="mt-2 border-[#FFD700]/30 text-[#FFD700]">{product.category_name}</Badge>}
          </div>

          <div className="flex items-baseline gap-3">
            {product.has_discount && <span className="text-xl text-zinc-500 line-through">{formatMoney(product.price)}</span>}
            <span className="text-4xl font-bold text-[#FFD700]">{formatMoney(product.current_price)}</span>
          </div>

          <div className="flex items-center gap-4">
            <span className="text-zinc-400">Stock:</span>
            <span className={`font-semibold ${product.stock > 0 ? 'text-green-500' : 'text-red-500'}`}>{product.stock} available</span>
          </div>

          {product.description && (
            <Card className="bg-[#121212] border-white/10">
              <CardContent className="p-4">
                <h3 className="font-semibold text-white mb-2">Description</h3>
                <p className="text-zinc-400 whitespace-pre-wrap">{product.description}</p>
              </CardContent>
            </Card>
          )}

          <div className="flex items-center gap-4">
            <div className="flex items-center border border-white/10 rounded-lg">
              <Button variant="ghost" size="icon" onClick={() => setQuantity(Math.max(1, quantity - 1))} disabled={quantity <= 1}>
                <Minus className="w-4 h-4" />
              </Button>
              <Input type="number" value={quantity} onChange={(e) => setQuantity(Math.max(1, Math.min(product.stock, parseInt(e.target.value) || 1)))} className="w-16 text-center border-0 bg-transparent" min="1" max={product.stock} />
              <Button variant="ghost" size="icon" onClick={() => setQuantity(Math.min(product.stock, quantity + 1))} disabled={quantity >= product.stock}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <Button onClick={handleAddToCart} disabled={product.stock === 0 || addingToCart} className="flex-1 bg-[#FFD700] text-black hover:bg-[#E5C100] font-bold gold-glow" data-testid="add-to-cart-btn">
              {addingToCart ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShoppingCart className="w-4 h-4 mr-2" />}
              Add to Cart - {formatMoney(product.current_price * quantity)}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
